/**
 * Coalescing of history entries (W1-09 state-store; ARCHITECTURE.md §5.3, DESIGN.md §5.13).
 *
 * A commit merges into the top history entry (one undo step) when all of these hold:
 *  - it carries a `coalesceKey` equal to the top entry's key;
 *  - the top entry is the current position (nothing undone above it) and was not sealed;
 *  - nothing was committed in between: the top entry's last transaction is the store's last committed seq
 *    (an undo, redo or any other transaction breaks the run);
 *  - it comes within 800 ms of the previous transaction of the run (each step restarts the window, so a run of
 *    nudges 500 ms apart is one step however long it lasts).
 *
 * Keys in use: `field:<entity>:<field>` (typing in one field; the inspector seals on blur, so typing is one step
 * per focus), `nudge:<selection>`, `wheel:<selection>`, `offset-nudge`, `bpm-tap`. `CoalesceKeys` builds them;
 * selection keys use `selectionKey`, which is stable for equal selections.
 */

import type { SelectionSnapshot } from '../core/ops/types';

/** The coalescing window (ms) between two consecutive transactions of a run. */
export const COALESCE_WINDOW_MS = 800;

/** What the coalescing decision needs to know about the top history entry. */
export interface CoalesceTarget {
  readonly coalesceKey: string | undefined;
  /** Seq of the last transaction merged into (or pushed as) the entry; -1 when unknown (restored history). */
  readonly lastSeq: number;
  /** Epoch ms of that transaction. */
  readonly lastAt: number;
  /** Set when the run was ended explicitly (field blur, gesture end of a typing session...). */
  readonly sealed: boolean;
}

export interface CoalesceQuery {
  readonly key: string | undefined;
  /** Epoch ms of the new transaction. */
  readonly now: number;
  /** The entry at the current history position, or null. */
  readonly top: CoalesceTarget | null;
  /** True when the entry at the current position is the newest one (no redo tail). */
  readonly atTop: boolean;
  /** The store's last committed seq (before the new transaction). */
  readonly lastSeq: number;
  readonly windowMs?: number;
}

/** True when the new transaction merges into the top history entry. */
export function canCoalesce(q: CoalesceQuery): boolean {
  const { key, top } = q;
  if (key === undefined || key === '' || top === null || !q.atTop || top.sealed) return false;
  if (top.coalesceKey !== key) return false;
  if (top.lastSeq < 0 || top.lastSeq !== q.lastSeq) return false;
  const dt = q.now - top.lastAt;
  return dt >= 0 && dt <= (q.windowMs ?? COALESCE_WINDOW_MS);
}

const KINDS = ['notes', 'keys', 'segments', 'lines', 'bpm', 'markers', 'effects', 'videos', 'groups'] as const;

/** 32-bit FNV-1a of a string, as 8 hex digits. */
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * Stable key of a selection (ids per kind in ascending order; the active line is not part of it). Short selections
 * are spelled out; long ones are hashed with their size, e.g. `n1,5,9`, `n1,2;k7`, `#3f2a9c01:1204`.
 */
export function selectionKey(s: SelectionSnapshot): string {
  const parts: string[] = [];
  let count = 0;
  for (const kind of KINDS) {
    const ids = s[kind] as readonly number[];
    if (ids.length === 0) continue;
    count += ids.length;
    const sorted = [...ids].sort((a, b) => a - b);
    parts.push(`${kind.charAt(0)}${kind === 'segments' ? 's' : ''}${sorted.join(',')}`);
  }
  const text = parts.join(';');
  return text.length <= 96 ? text : `#${fnv1a(text)}:${String(count)}`;
}

/** Builders of the coalescing keys in use (ARCHITECTURE.md §5.3). */
export const CoalesceKeys = {
  /** Typing in one inspector field of one entity, e.g. `field:note:12:x`. */
  field: (entity: string, field: string): string => `field:${entity}:${field}`,
  /** Arrow-key / bracket nudges of the current selection. */
  nudge: (sel: SelectionSnapshot): string => `nudge:${selectionKey(sel)}`,
  /** Wheel fine-tuning of the current selection. */
  wheel: (sel: SelectionSnapshot): string => `wheel:${selectionKey(sel)}`,
  /** Offset nudges in the Timing workspace. */
  offsetNudge: 'offset-nudge',
  /** Tap-tempo BPM updates. */
  bpmTap: 'bpm-tap',
} as const;
